export function generateFormRowActionsFile(params: {
  actions: string
  deleteDialog: string
  deleteDialogFileName: string
}): string {
  const { actions, deleteDialog, deleteDialogFileName } = params

  return `'use client'

import { Button } from '@admin/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from '@admin/components/ui/dropdown-menu'
import { MoreHorizontal } from 'lucide-react'
import * as React from 'react'
import { ${deleteDialog} } from './${deleteDialogFileName}'

export function ${actions}({ id }: { id: string }) {
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [deleteOpen, setDeleteOpen] = React.useState(false)

  function handleDeleteOpenChange(open: boolean) {
    setDeleteOpen(open)

    if (!open) {
      setMenuOpen(false)
    }
  }

  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="size-8 p-0"
          aria-label={\`Open submission actions for \${id}\`}
        >
          <span className="sr-only">Open submission actions</span>
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <${deleteDialog}
          id={id}
          open={deleteOpen}
          onOpenChange={handleDeleteOpenChange}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
`
}

export function generateFormDeleteDialogFile(params: {
  pascal: string
  camel: string
  actionImportPath: string
}): string {
  const { pascal, camel, actionImportPath } = params

  return `'use client'

import { delete${pascal}Submission } from '@admin/actions/${actionImportPath}'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@admin/components/ui/alert-dialog'
import { DropdownMenuItem } from '@admin/components/ui/dropdown-menu'
import { useQueryClient } from '@tanstack/react-query'
import { Trash } from 'lucide-react'
import * as React from 'react'
import { toast } from 'sonner'

interface Delete${pascal}SubmissionDialogProps {
  id: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function Delete${pascal}SubmissionDialog({
  id,
  open,
  onOpenChange
}: Delete${pascal}SubmissionDialogProps) {
  const [isPending, startTransition] = React.useTransition()
  const queryClient = useQueryClient()

  const handleDelete = () => {
    startTransition(async () => {
      try {
        const result = await delete${pascal}Submission(id)
        if (result.success) {
          toast.success('Submission deleted successfully')
          queryClient.refetchQueries({ queryKey: ['${camel}-submissions'] })
          onOpenChange(false)
        } else {
          toast.error(result.error || 'Failed to delete submission')
        }
      } catch (error) {
        toast.error('An error occurred')
        console.error(error)
      }
    })
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogTrigger asChild>
        <DropdownMenuItem variant="destructive" onSelect={(event) => event.preventDefault()}>
          <Trash className="size-4" />
          Delete submission
        </DropdownMenuItem>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete this submission.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault()
              handleDelete()
            }}
            disabled={isPending}
          >
            {isPending ? 'Deleting...' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
`
}
